import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'react-alert';
import Button from 'shared/components/Button';
import ProjectModal from '../Home/components/ProjectModal';
import FiltersMenu from './components/FiltersMenu';
import SaveToProjectModal from 'shared/components/likiee/SaveToProjectModal';
import AddProductModal from 'shared/components/likiee/AddProductModal';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import SelectAndAddProductToProject from 'shared/components/likiee/SelectAndAddProductToProject';
import ProductsHeader from './components/ProductsHeader';
import NoProductsData from './components/NoProductsData';
import ProductList from './components/ProductList';
import { getError } from 'utils/appHelpers';
import { API, graphqlOperation } from 'aws-amplify';
import {
  getLikieesFiltersQuery,
  getLikieesInFiltersQuery,
  searchLikieesInQuery,
  searchLikieesQuery,
} from 'graphql/likiees/queries';
import useScrollEnd from 'shared/hooks/useScrollEnd';
import { subscribeToProductChanges, unSubscribeProductChanges } from '../../actions/products';
import { onSetProjectItems } from '../../actions/projectItems';
import { getProjects } from '../../actions/projects';
import AddProductByImageModal from 'shared/components/likiee/AddProductByImageModal';
import useDidMountEffect from 'shared/hooks/useDidMountEffect';

// Utility function to safely find index in an array that might contain null items
const safeFindIndex = (array, predicate, context = 'unknown') => {
  try {
    if (!Array.isArray(array)) {
      return -1;
    }

    return array.findIndex((item, index) => {
      try {
        if (item === null || item === undefined) {
          return false;
        }
        return predicate(item, index);
      } catch (error) {
        return false;
      }
    });
  } catch (error) {
    return -1;
  }
};

const emptyFilters = {
  categories: [],
  projects: [],
  source: [],
  brands: [],
  minPrice: null,
  maxPrice: null,
  // withPriceAlert: true,
};

// Utility function to clean products array of any null/invalid items
const cleanProductsArray = (products, context = 'unknown') => {
  try {
    if (!products || !products.items || !Array.isArray(products.items)) {
      return products;
    }

    const originalLength = products.items.length;
    const cleanItems = products.items.filter(item => {
      if (!item || typeof item !== 'object' || !item.id) {
        return false;
      }
      return true;
    });

    if (cleanItems.length !== originalLength) {
      return {
        ...products,
        items: cleanItems,
        total: Math.max(0, cleanItems.length), // Ensure total matches clean items count
      };
    }

    return products;
  } catch (error) {
    return products; // Return original on error
  }
};

const Products = ({ match }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const projectID = match?.params?.id;
  const projects = useSelector(state => state.projects);
  const projectItems = useSelector(state => state.projectItems);
  const project = projects?.[projectID];
  const container = useRef(window);
  const loadState = useRef({ canLoad: false });
  const isMountedRef = useRef(true);

  const [sorting, setSorting] = useState(true);
  const [addProductState, setAddProductState] = useState(false);
  const [addProductPhotoState, setAddProductPhotoState] = useState(false);
  const [organizeState, setOrganizeState] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [addFromLikieeState, setAddFromLikieeState] = useState(false);
  const [saveToProjectState, setSaveToProjectState] = useState(false);
  const [fetching, setFetching] = useState(false);
  const alert = useAlert();

  const initialProducts = projectItems[projectID || 'all'] || null;
  // Ensure we never have null items in the initial state
  const safeInitialProducts =
    initialProducts && initialProducts.items
      ? {
          ...initialProducts,
          items: initialProducts.items.filter(item => item && typeof item === 'object' && item.id),
        }
      : initialProducts;

  const [products, setProducts] = useState(safeInitialProducts);
  const [productForProject, setProductForProject] = useState(false);
  const [editModalState, setEditModalState] = useState();
  const [filtersMenu, setFiltersMenu] = useState(false);
  const [filtersData, setFiltersData] = useState();
  const [filters, setFilters] = useState({ ...emptyFilters });

  const getFiltersData = async products => {
    try {
      if (!products) return;
      const parsedProducts = products?.filter(i => i.status !== 'PENDING' && i.status !== 'EXTRACTING');
      if (!parsedProducts?.length) return;
      const body = {
        aggregates: [
          { field: 'brand', name: 'brand', type: 'terms' },
          { field: 'likieeSourceId', name: 'source', type: 'terms' },
          { field: 'price', name: 'minPrice', type: 'min' },
          { field: 'price', name: 'maxPrice', type: 'max' },
          { field: 'categoryTags', name: 'categories', type: 'terms' },
        ],
      };
      if (project) body.projects = [project.id];
      const query = project ? getLikieesInFiltersQuery : getLikieesFiltersQuery;
      const { data } = await API.graphql(graphqlOperation(query, body));
      const resData = data.searchLikiees || data.searchLikieesIn;
      const res = {};
      resData?.aggregateItems.forEach(item => {
        res[item.name] = item.result?.value || item.result?.buckets;
      });
      if (Object.keys(res)?.length) setFiltersData(res);
    } catch (err) {
      alert.error(getError(err));
    }
  };

  const getSearchFilters = () => {
    const body = { limit: 20, sort: { direction: sorting ? 'desc' : 'asc', field: 'createTime' } };
    const andFilter = [];

    if (filters.minPrice || filters.maxPrice) {
      const price = {};
      if (filters.minPrice) price.gte = filters.minPrice;
      if (filters.maxPrice) price.lte = filters.maxPrice;
      andFilter.push({ or: { price } });
    }

    if (filters.categories?.length) {
      const categorieIds = filters.categories.map(i => ({ categoryTags: { eq: i } }));
      andFilter.push({ or: categorieIds });
    }

    if (filters.source?.length) {
      const sourceIds = filters.source.map(i => ({ likieeSourceId: { matchPhrasePrefix: i } }));
      andFilter.push({ or: sourceIds });
    }

    if (filters.brands?.length) {
      const brandIds = filters.brands.map(i => ({ brand: { matchPhrasePrefix: i } }));
      andFilter.push({ or: brandIds });
    }

    if (andFilter.length) {
      body.filter = { and: andFilter };
    }

    if (filters.projects?.length) {
      body.projects = [...filters.projects];
    }

    if (project) {
      body.projects = [project.id];
    }

    return body;
  };

  const getProductsData = async hideLoading => {
    if (!isMountedRef.current) return;

    try {
      if (isMountedRef.current) {
        setFetching(!hideLoading);
      }
      const body = { ...getSearchFilters() };
      const query = body.projects ? searchLikieesInQuery : searchLikieesQuery;
      const { data } = await API.graphql(graphqlOperation(query, body));
      const response = data.searchLikiees || data.searchLikieesIn;

      if (!isMountedRef.current) return;

      dispatch(onSetProjectItems({ projectID: projectID || 'all', body: response }));
      setProducts(response || {});
      loadState.current.canLoad = response?.items?.length < response?.total;
      getFiltersData(response?.items);
    } catch (err) {
      if (isMountedRef.current) {
        alert.error(getError(err));
      }
    } finally {
      if (isMountedRef.current) {
        setFetching(false);
      }
    }
  };

  const getNextProductsData = async nextToken => {
    if (!isMountedRef.current) return;

    try {
      loadState.current.canLoad = false;
      const body = { ...getSearchFilters(), nextToken };
      const query = body.projects ? searchLikieesInQuery : searchLikieesQuery;
      const { data } = await API.graphql(graphqlOperation(query, body));
      const response = data.searchLikiees || data.searchLikieesIn;

      if (!isMountedRef.current) return;

      setProducts(products => {
        const merged = { ...response, items: [...products.items, ...response.items] };
        loadState.current.canLoad = merged.items.length < merged.total;
        loadState.current.token = merged.nextToken;
        return merged;
      });
    } catch (err) {
      if (isMountedRef.current) {
        alert.error(getError(err));
      }
    }
  };

  const onDeleteProductSuccess = product => {
    if (!product || !product.id) {
      console.warn('onDeleteProductSuccess: Invalid product received', product);
      return;
    }
    if (!isMountedRef.current) {
      return;
    }

    setProducts(products => {
      if (!products || !products.items) {
        return products;
      }

      // Filter out any null/undefined items and also remove the target product
      const validItems = products.items.filter(
        item => item && typeof item === 'object' && item.id && item.id !== product.id,
      );
      const hasRemoveItem = validItems.length !== products.items.length;
      const total = hasRemoveItem ? Math.max(0, products.total - 1) : products.total;

      const result = { ...products, total, items: validItems };
      return result;
    });
  };

  const onUpdateProductSuccess = product => {
    if (!product || !product.id) {
      console.warn('onUpdateProductSuccess: Invalid product received', product);
      return;
    }
    if (!isMountedRef.current) {
      return;
    }

    try {
      setProducts(products => {
        try {
          if (!products || !products.items) {
            return products;
          }

          // Filter out any null/undefined items before processing
          const validItems = products.items.filter(item => item && typeof item === 'object' && item.id);

          const index = safeFindIndex(validItems, i => i.id === product.id, 'onUpdateProductSuccess');

          let updatedItems;
          if (index >= 0) {
            updatedItems = [...validItems];
            updatedItems[index] = { ...updatedItems[index], ...product };
          } else {
            updatedItems = validItems;
          }

          const result = { ...products, items: updatedItems };
          return result;
        } catch (innerError) {
          return products; // Return original state on error
        }
      });
    } catch (outerError) {
      // Silent error handling
    }
  };

  const onAddroductSuccess = product => {
    if (!product || !product.id) {
      console.warn('onAddroductSuccess: Invalid product received', product);
      return;
    }
    if (!isMountedRef.current) {
      return;
    }

    setProducts(products => {
      if (!products) {
        return { total: 1, items: [product] };
      }

      // Filter out any null/undefined items first
      const validItems = (products.items || []).filter(item => item && typeof item === 'object' && item.id);

      // Check if product already exists
      const existingIndex = safeFindIndex(validItems, item => item.id === product.id, 'onAddroductSuccess');
      if (existingIndex >= 0) {
        return products;
      }

      const newTotal = Math.max(1, (products.total || 0) + 1);
      const updatedItems = [product, ...validItems];

      const result = { ...products, total: newTotal, items: updatedItems };
      return result;
    });
  };

  const onSaveToProjectSuccess = () => {
    setSaveToProjectState(false);
    setSelectedProducts([]);
  };

  const onReloadData = () => {
    if (!projectID || (projectID && project)) {
      getFiltersData(products?.items);
      getProductsData();
    }
  };

  const onClickProductProjectHandler = product => {
    setProductForProject(product);
  };

  useScrollEnd(container, loadState, token => {
    getNextProductsData(token);
  });

  useEffect(() => {
    if (products?.items?.length && (!projectID || (projectID && project))) {
      getFiltersData(products?.items);
    }
  }, [project]);

  useDidMountEffect(() => {
    if (!projectID || (projectID && project)) {
      getProductsData();
    }
  }, [sorting, filters, project]);

  useEffect(() => {
    getProductsData(!!products);
  }, []);

  useEffect(() => {
    if (!projects) getProjects();

    const handleAddProduct = product => {
      if (isMountedRef.current) {
        onAddroductSuccess(product);
      }
    };

    const handleUpdateProduct = product => {
      if (isMountedRef.current) {
        onUpdateProductSuccess(product);
      }
    };

    const handleDeleteProduct = product => {
      if (isMountedRef.current) {
        onDeleteProductSuccess(product);
      }
    };

    subscribeToProductChanges({
      onAddProduct: handleAddProduct,
      onUpdateProduct: handleUpdateProduct,
      onDeleteProduct: handleDeleteProduct,
    });

    return () => {
      unSubscribeProductChanges();
    };
  }, []);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safety cleanup effect to ensure products array is always clean
  useEffect(() => {
    if (products && products.items) {
      const cleanedProducts = cleanProductsArray(products, 'safety-effect');
      if (cleanedProducts !== products) {
        setProducts(cleanedProducts);
      }
    }
  }, [products]);

  if (projectID && !project) return <SkeletonLoader type='product-grid' count={8} />;

  const hasSelectedFilters = Object.keys(filters).some(key => {
    if (Array.isArray(filters[key])) return filters[key].length;
    return filters[key];
  });

  return (
    <div className='products-page pb-10'>
      <ProductsHeader
        productsCount={products?.total || 0}
        project={project}
        organizeState={organizeState}
        onAddLikieeClick={() => setAddProductState(true)}
        onAddLikieeImageClick={() => setAddProductPhotoState(true)}
        onOrganizeClick={() => setOrganizeState(!organizeState)}
        onFilterClick={() => setFiltersMenu(!filtersMenu)}
        onEditProjectClick={() => setEditModalState(true)}
        onSortClick={() => setSorting(!sorting)}
        disableFilterButton={!filtersData}
        disableSortButton={!products?.items?.length}
        disableOrganizeButton={!products?.items?.length}
        hasSelectedFilters={hasSelectedFilters}
      />
      {!products || fetching ? (
        <SkeletonLoader type='product-grid' count={8} />
      ) : (
        <ProductList
          onClickProductProject={onClickProductProjectHandler}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          isOnOrganizeState={organizeState}
          products={products}
          onProductRemoveSuccess={onDeleteProductSuccess}
          container={container}
        />
      )}
      {!products?.items?.length && !fetching && (
        <NoProductsData
          hideAddFromList={!project?.name}
          onClickAddProductUrl={() => setAddProductState(true)}
          onClickAddProductList={() => setAddFromLikieeState(true)}
        />
      )}
      {editModalState && <ProjectModal projectModalId={match.params?.id} onClose={() => setEditModalState(false)} />}
      {filtersMenu && filtersData && (
        <FiltersMenu
          onClose={() => setFiltersMenu(false)}
          filtersData={filtersData}
          showProjects={!project}
          filters={filters}
          setFilters={setFilters}
          hasSelectedFilters={hasSelectedFilters}
          onResetFilters={() => {
            setFilters(emptyFilters);
            setFiltersMenu(false);
          }}
        />
      )}
      {productForProject && (
        <SaveToProjectModal
          productID={productForProject.id}
          initialProjectIDs={productForProject.projects?.items.map(i => i.id) || []}
          onClose={() => {
            setProductForProject(false);
          }}
          onSuccess={onReloadData}
        />
      )}
      {addProductState && (
        <AddProductModal customProjectID={match.params.id} onClose={() => setAddProductState(false)} />
      )}
      {addProductPhotoState && (
        <AddProductByImageModal customProjectID={match.params.id} onClose={() => setAddProductPhotoState(false)} />
      )}
      {addFromLikieeState && (
        <SelectAndAddProductToProject
          projectID={match.params.id}
          onSuccess={onReloadData}
          onClose={() => {
            setAddFromLikieeState(false);
          }}
        />
      )}
      {organizeState && (
        <div className='products-organize py-15 b-0 bg-white l-0 position-fixed z-index-100 w-100 shadow'>
          <div className='container d-flex align-items-center justify-content-end'>
            <p className='mb-0'>{t('selected_products', { count: selectedProducts.length })}</p>
            <Button className='btn-primary ml-16 btn-sm' onClick={() => setSaveToProjectState(true)}>
              {t('btn_save_to')}
            </Button>
          </div>
        </div>
      )}
      {saveToProjectState && (
        <SaveToProjectModal
          title={t('title_save_to')}
          productIDS={selectedProducts}
          onClose={() => setSaveToProjectState(false)}
          onSuccess={onSaveToProjectSuccess}
        />
      )}
    </div>
  );
};

export default Products;
